// @flow

// Based on agency-pages/src/components/rating.tsx
import * as React from 'react';
import { Flex, Box } from 'react-system';
import { v4 as uuidv4 } from 'uuid';
import { useTheme } from '../../theme';
import { useLocale } from '../../hooks/locale';
import { Star } from '../../icons/star';

const PartialStar = ({
  size,
  fillPercentage,
  fillColor,
  backgroundColor,
}: {|
  size: number,
  fillPercentage: number,
  fillColor: string,
  backgroundColor: string,
|}) => {
  // Ensure the fill percentage is between 0 and 100
  const validFillPercentage = Math.min(100, Math.max(0, fillPercentage));
  // Calculate the stop point for the gradient in the SVG
  const stopPoint = `${validFillPercentage}%`;
  const gradientId = `partialFillGradient-${uuidv4()}`;

  return (
    <svg
      viewBox="0 0 10 9"
      width="1em"
      height="1em"
      fontSize={size}
      fill="currentColor"
      css={{ display: 'block', flex: '0 0 auto', color: fillColor }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset={stopPoint} stopColor="currentColor" />
          <stop offset={stopPoint} stopColor={backgroundColor} />
        </linearGradient>
      </defs>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        fill={`url(#${gradientId})`}
        d="M4.87842 0.180176L6.28476 3.02927L9.42969 3.48895L7.15406 5.70542L7.69111 8.8367L4.87842 7.35753L2.06573 8.8367L2.60278 5.70542L0.327148 3.48895L3.47208 3.02927L4.87842 0.180176Z"
      ></path>
    </svg>
  );
};

const StarRating = ({ rating }: {| rating: number |}): React.Node => {
  const { colors } = useTheme();
  // Calculate the number of full stars
  const fullStars = Math.floor(rating);
  // Calculate the value of the partial star
  const partialStarValue = rating - fullStars;
  // Determine if there's a partial star (more than 0 but less than 1)
  const hasPartialStar = partialStarValue > 0;
  // Calculate the number of empty stars
  const emptyStars = hasPartialStar ? 4 - fullStars : 5 - fullStars;

  return (
    <Flex alignItems="center">
      {[...Array(fullStars)].map((_, index) => {
        return (
          <Star
            key={`full-${index}`}
            size={14}
            css={{ color: colors.yellow600 }}
          />
        );
      })}
      {hasPartialStar && (
        <PartialStar
          size={14}
          fillPercentage={partialStarValue * 100}
          fillColor={colors.yellow600}
          backgroundColor={colors.grey300}
        />
      )}
      {[...Array(emptyStars)].map((_, index) => (
        <Star
          key={`empty-${index}`}
          size={14}
          css={{ color: colors.grey300 }}
        />
      ))}
    </Flex>
  );
};

export const RatingComponent = ({
  rating,
  reviewsCount,
}: {|
  rating: ?number,
  reviewsCount: ?number,
  css?: any,
|}): React.Node => {
  const { t } = useLocale();
  const { text } = useTheme();
  const roundedRating = rating != null ? Math.round(rating * 10) / 10 : 0;

  return (
    <>
      {roundedRating > 0 && reviewsCount != null && reviewsCount > 0 && (
        <Flex alignItems="center" flexWrap="wrap" css={{ columnGap: '6px' }}>
          <StarRating rating={roundedRating} />
          <Box css={[text.subtitle2]}>{roundedRating.toFixed(1)}</Box>
          <Box css={[text.caption, { lineHeight: 1 }]}>
            {t('reviews', { count: reviewsCount })}
          </Box>
        </Flex>
      )}
    </>
  );
};
